import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileSpreadsheet, Plus, Play } from "lucide-react";
import { toast } from "sonner";

const Input = (p) => <input {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 w-full" />;
const Sel = ({ children, ...p }) => <select {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white outline-none w-full">{children}</select>;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;

function monthRange(offset = 0) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end), label: start.toLocaleDateString("en-US", { month: "long", year: "numeric" }) };
}

export default function Settlements() {
  const { store } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [periodType, setPeriodType] = useState("monthly");
  const [range, setRange] = useState(monthRange(0));
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const cur = store?.currency || "USD";

  const load = () => { setRows(null); api.get("/settlements").then((r) => setRows(r.data)); };
  useEffect(() => { load(); }, [store?.id]);
  useEffect(() => { if (params.get("start")) setOpen(true); }, [params]);

  const runPreview = async () => {
    setLoadingPreview(true); setPreview(null);
    try {
      const { data } = await api.post("/settlements/preview", { period_type: periodType, start_date: range.start, end_date: range.end });
      setPreview(data);
    } catch (e) { toast.error("Preview failed"); } finally { setLoadingPreview(false); }
  };
  useEffect(() => { if (open) runPreview(); }, [open, range.start, range.end]);

  const create = async () => {
    try {
      const { data } = await api.post("/settlements", { period_type: periodType, start_date: range.start, end_date: range.end, label: range.label });
      toast.success("Settlement created"); setOpen(false); navigate(`/settlements/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Settlements" subtitle="Close your books and pay consignors">
        <button data-testid="new-settlement-btn" onClick={() => { setRange(monthRange(0)); setOpen(true); }}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <Plus className="w-4 h-4" /> Start Settlement
        </button>
      </PageHeader>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <EmptyState icon={FileSpreadsheet} title="No settlements yet" description="Start a settlement to review consignor payouts for a period." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Period</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-center">Consignors</th>
                <th className="px-4 py-3 text-right">Net Sales</th><th className="px-4 py-3 text-right">Amount Owed</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s, i) => (
                <tr key={s.id} data-testid={`settlement-row-${i}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/settlements/${s.id}`)}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.label}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{s.period_type}</td>
                  <td className="px-4 py-3 text-center">{s.consignor_count}</td>
                  <td className="px-4 py-3 text-right font-mono-fin">{money(s.totals.net_sales, cur)}</td>
                  <td className="px-4 py-3 text-right font-mono-fin font-semibold text-amber-700">{money(s.totals.amount_owed, cur)}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="settlement-preview-dialog">
          <DialogHeader><DialogTitle>Start Settlement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div><Label>Period</Label>
                <Sel data-testid="period-type-select" value={periodType} onChange={(e) => { setPeriodType(e.target.value); if (e.target.value === "monthly") setRange(monthRange(0)); }}>
                  <option value="monthly">Monthly</option><option value="weekly">Weekly</option><option value="custom">Custom</option>
                </Sel></div>
              {periodType === "monthly" ? (
                <div className="sm:col-span-3"><Label>Month</Label>
                  <Sel data-testid="month-select" onChange={(e) => setRange(monthRange(Number(e.target.value)))}>
                    {[0, -1, -2, -3].map((o) => <option key={o} value={o}>{monthRange(o).label}</option>)}
                  </Sel></div>
              ) : (
                <>
                  <div><Label>Start</Label><Input data-testid="start-date" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value, label: `${e.target.value} to ${range.end}` })} /></div>
                  <div><Label>End</Label><Input data-testid="end-date" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value, label: `${range.start} to ${e.target.value}` })} /></div>
                </>
              )}
            </div>

            {loadingPreview ? <Spinner label="Calculating…" /> : preview && (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  {[["Gross", preview.totals.gross_sales], ["Net", preview.totals.net_sales], ["Consignor Share", preview.totals.consignor_share], ["Store Share", preview.totals.store_share]].map(([l, v]) => (
                    <div key={l} className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">{l}</p><p className="font-mono-fin font-bold text-slate-900">{money(v, cur)}</p></div>
                  ))}
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="preview-table">
                    <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-xs font-semibold uppercase text-slate-500">
                      <th className="px-3 py-2">Consignor</th><th className="px-3 py-2 text-center">Items</th><th className="px-3 py-2 text-right">Net</th>
                      <th className="px-3 py-2 text-center">%</th><th className="px-3 py-2 text-right">Owed</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.rows.map((r) => (
                        <tr key={r.consignor_id}>
                          <td className="px-3 py-2 font-medium">{r.consignor_name}</td>
                          <td className="px-3 py-2 text-center">{r.items_sold}</td>
                          <td className="px-3 py-2 text-right font-mono-fin">{money(r.net_sales, cur)}</td>
                          <td className="px-3 py-2 text-center">{r.payout_pct}%</td>
                          <td className="px-3 py-2 text-right font-mono-fin font-semibold text-amber-700">{money(r.amount_owed, cur)}</td>
                        </tr>
                      ))}
                      {preview.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No sales in this period.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="create-settlement-btn" onClick={create} disabled={!preview || preview.rows.length === 0}
              className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
              <Play className="w-4 h-4" /> Create Settlement
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
